import React, { useEffect, useState } from 'react';
import { LinkPromptDialog } from '../../common/LinkPromptDialog';
import {
    Box,
    Typography,
    IconButton,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Paper,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatListBulleted,
    FormatListNumbered,
    FormatQuote,
    Link as LinkIcon,
    FormatClear,
    Code as CodeIcon,
} from '@mui/icons-material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { ListItemWithStyle } from './extensions/ListItemWithStyle';

interface RichTextInputProps {
    label: string;
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
    helperText?: string;
    minHeight?: number;
}

const RichTextInput: React.FC<RichTextInputProps> = ({
    label,
    value,
    onChange,
    placeholder = '',
    helperText,
    minHeight = 150,
}) => {
    const editor = useEditor({
        extensions: [
            // #310-Nachbesserung: wie im InfoTextEditor (#141) — Marker von
            // Aufzaehlungen sollen Bold/Italic der Zeile uebernehmen.
            StarterKit.configure({ listItem: false }),
            ListItemWithStyle,
            TiptapLink.configure({ openOnClick: false }),
            Underline,
        ],
        content: value || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm focus:outline-none',
                style: `min-height: ${minHeight}px; padding: 12px;`
            },
        },
    });

    // Sync external value changes (e.g. when loading saved data)
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || '');
        }
    }, [value]);

    if (!editor) {
        return null;
    }

    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const openLinkDialog = () => setLinkDialogOpen(true);
    const applyLink = (url: string | null) => {
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {label}
            </Typography>
            <Paper variant="outlined">
                <Box sx={{
                    p: 0.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    alignItems: 'center',
                }}>
                    {/* Text style / heading dropdown */}
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                            value={
                                editor.isActive('heading', { level: 1 }) ? 'h1'
                                : editor.isActive('heading', { level: 2 }) ? 'h2'
                                : editor.isActive('heading', { level: 3 }) ? 'h3'
                                : editor.isActive('heading', { level: 4 }) ? 'h4'
                                : 'paragraph'
                            }
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'paragraph') {
                                    editor.chain().focus().setParagraph().run();
                                } else {
                                    const level = parseInt(val.replace('h', '')) as 1 | 2 | 3 | 4;
                                    editor.chain().focus().toggleHeading({ level }).run();
                                }
                            }}
                            size="small"
                            sx={{ fontSize: '0.8rem', height: 32 }}
                        >
                            <MenuItem value="paragraph" sx={{ fontSize: '0.875rem' }}>Normal</MenuItem>
                            <MenuItem value="h1" sx={{ fontSize: '1.5rem', fontWeight: 700 }}>Überschrift 1</MenuItem>
                            <MenuItem value="h2" sx={{ fontSize: '1.25rem', fontWeight: 700 }}>Überschrift 2</MenuItem>
                            <MenuItem value="h3" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Überschrift 3</MenuItem>
                            <MenuItem value="h4" sx={{ fontSize: '1rem', fontWeight: 600 }}>Überschrift 4</MenuItem>
                        </Select>
                    </FormControl>

                    <Divider orientation="vertical" flexItem />

                    <ToggleButtonGroup size="small">
                        <ToggleButton
                            value="bold"
                            selected={editor.isActive('bold')}
                            onClick={() => editor.chain().focus().toggleBold().run()}
                        >
                            <FormatBold fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                            value="italic"
                            selected={editor.isActive('italic')}
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                        >
                            <FormatItalic fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                            value="underline"
                            selected={editor.isActive('underline')}
                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                        >
                            <FormatUnderlined fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Divider orientation="vertical" flexItem />

                    <ToggleButtonGroup size="small">
                        <ToggleButton
                            value="bulletList"
                            selected={editor.isActive('bulletList')}
                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                        >
                            <FormatListBulleted fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                            value="orderedList"
                            selected={editor.isActive('orderedList')}
                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        >
                            <FormatListNumbered fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Divider orientation="vertical" flexItem />

                    <ToggleButtonGroup size="small">
                        <ToggleButton
                            value="blockquote"
                            selected={editor.isActive('blockquote')}
                            onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        >
                            <FormatQuote fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                            value="codeBlock"
                            selected={editor.isActive('codeBlock')}
                            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        >
                            <CodeIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <IconButton
                        size="small"
                        onClick={openLinkDialog}
                        color={editor.isActive('link') ? 'primary' : 'default'}
                    >
                        <LinkIcon fontSize="small" />
                    </IconButton>
                    <LinkPromptDialog
                        open={linkDialogOpen}
                        initialUrl={editor.getAttributes('link').href || ''}
                        onClose={() => setLinkDialogOpen(false)}
                        onSubmit={applyLink}
                    />

                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                    >
                        <FormatClear fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{
                    minHeight,
                    '& .ProseMirror': {
                        outline: 'none',
                        padding: 1.5,
                        '& h1': { fontSize: '2em', fontWeight: 700, margin: '0.67em 0' },
                        '& h2': { fontSize: '1.5em', fontWeight: 700, margin: '0.75em 0' },
                        '& h3': { fontSize: '1.17em', fontWeight: 600, margin: '0.83em 0' },
                        '& h4': { fontSize: '1em', fontWeight: 600, margin: '1em 0' },
                        // Abstaende identisch zu RichTextContent (Player), damit
                        // Editor-Vorschau und Lerninhalt gleich aussehen.
                        '& p': { marginBottom: 1 },
                        '& ul, & ol': { paddingLeft: 3, marginBottom: 1 },
                        '& li::marker': {
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            fontStyle: 'inherit',
                        },
                        '& pre': { backgroundColor: '#f5f5f5', padding: '1em', borderRadius: '4px', overflow: 'auto' },
                        '& code': { backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace' },
                        '& blockquote': {
                            borderLeft: '4px solid #e0e0e0',
                            paddingLeft: 2,
                            fontStyle: 'italic',
                            marginLeft: 0,
                            color: 'text.secondary'
                        },
                        '& a': { color: 'primary.main', textDecoration: 'underline' },
                    },
                    '& .ProseMirror p.is-editor-empty:first-child::before': {
                        content: `"${placeholder}"`,
                        color: '#adb5bd',
                        pointerEvents: 'none',
                        float: 'left',
                        height: 0,
                    },
                }}>
                    <EditorContent editor={editor} />
                </Box>
            </Paper>
            {helperText && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {helperText}
                </Typography>
            )}
        </Box>
    );
};

export default RichTextInput;
